<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Detalle de CTS</h1>
    </div>

    <wz-alert v-if="noAccountCount" variant="warning" icon="ri-error-warning-line" class="mb-3">
      Hay {{ noAccountCount }} colaborador(es) sin cuenta CTS registrada. Complete el dato en la ficha del
      colaborador antes de confirmar.
    </wz-alert>

    <wz-card class="mb-4">
      <template #header>
        <div class="flex items-center justify-between w-full">
          <div class="wz-card-title">Resumen</div>
          <div class="flex gap-2">
            <wz-button variant="outline" @click="downloadXlsx">
              <i class="ri-download-2-line mr-1"></i>Exportar Excel
            </wz-button>
            <wz-button v-if="isBorrador" variant="primary" @click="confirmModal = true">
              <i class="ri-check-line mr-1"></i>Confirmar CTS
            </wz-button>
          </div>
        </div>
      </template>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Período</small>
          <strong>{{ periodLabel(result.periodType) }} {{ result.periodYear }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Empresa</small>
          <strong>{{ result.companyName || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Fecha de cálculo</small>
          <strong><span v-local-date="result.calculatedAt"></span></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Estado</small>
          <wz-badge :variant="statusVariant(result.status)" pill>{{ statusLabel(result.status) }}</wz-badge>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Total colaboradores</small>
          <strong>{{ result.totalEmployees != null ? result.totalEmployees : '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Monto total CTS</small>
          <strong><span v-decimal:pen="result.totalAmount"></span></strong>
        </div>
        <div v-if="result.confirmedAt" class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Fecha de confirmación</small>
          <strong><span v-local-date="result.confirmedAt"></span></strong>
        </div>
      </div>
    </wz-card>

    <wz-card title="Colaboradores">
      <wz-table>
        <thead>
          <tr>
            <th v-if="isBorrador" class="text-center">Incluir</th>
            <th>Colaborador</th>
            <th>DNI</th>
            <th>Fecha ingreso</th>
            <th class="text-right">Sueldo</th>
            <th class="text-right">Asig. F</th>
            <th class="text-right">1/6 Grat.</th>
            <th class="text-right">Rem. comp.</th>
            <th class="text-center">Meses</th>
            <th class="text-center">Días</th>
            <th class="text-center">Faltas</th>
            <th class="text-right">Ajuste</th>
            <th class="text-right">CTS</th>
            <th>Cuenta CTS</th>
            <th v-if="isBorrador" class="text-center">Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in result.details" :key="d.id" :class="{ 'row-excluded': d.isExcluded }">
            <td v-if="isBorrador" class="text-center">
              <input type="checkbox" :checked="!d.isExcluded" @change="toggleExclude(d)" />
            </td>
            <td>{{ d.employeeFullName }}</td>
            <td>{{ d.employeeDni }}</td>
            <td v-local-date="d.hireDate"></td>
            <td class="text-right"><span v-decimal:pen="d.baseSalary"></span></td>
            <td class="text-right"><span v-decimal:pen="d.familyAllowance"></span></td>
            <td class="text-right"><span v-decimal:pen="d.gratificationSixth"></span></td>
            <td class="text-right"><span v-decimal:pen="d.computableRemuneration"></span></td>
            <td class="text-center">{{ d.completeMonths }}</td>
            <td class="text-center">{{ d.extraDays }}</td>
            <td class="text-center">{{ d.unjustifiedAbsences || 0 }}</td>
            <td class="text-right">
              <span v-if="d.manualAdjustment" v-decimal:pen="d.manualAdjustment"></span>
              <span v-else>-</span>
            </td>
            <td class="text-right"><span v-decimal:pen="ctsPayable(d)"></span></td>
            <td>
              <template v-if="d.hasCtsAccount">
                {{ d.ctsBankName }} {{ maskAccount(d.ctsAccount) }}
              </template>
              <span v-else class="text-danger">
                <i class="ri-error-warning-line"></i> Sin cuenta CTS registrada
              </span>
            </td>
            <td v-if="isBorrador" class="text-center">
              <wz-tooltip text="Ajuste manual">
                <wz-button variant="outline" size="sm" @click="openAdjust(d)">
                  <i class="ri-edit-line"></i>
                </wz-button>
              </wz-tooltip>
            </td>
          </tr>
          <tr v-if="!result.details || !result.details.length">
            <td :colspan="isBorrador ? 15 : 13" class="text-center text-muted">Sin colaboradores en este período.</td>
          </tr>
        </tbody>
      </wz-table>
    </wz-card>

    <!-- Modal: ajuste manual -->
    <wz-modal v-model="adjustModal" title="Ajuste manual">
      <div v-if="adjustTarget">
        <p class="mb-2"><strong>{{ adjustTarget.employeeFullName }}</strong></p>
        <wz-input label="Ajuste (S/)" type="number" step="0.01" v-model.number="adjustForm.adjustment"
          placeholder="Monto positivo o negativo" class="mb-3" />
        <wz-textarea label="Motivo" required v-model="adjustForm.reason" :rows="3"
          placeholder="Motivo del ajuste" :error="adjustReasonError" />
      </div>
      <template #footer>
        <wz-button variant="outline" @click="adjustModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="saveAdjust">Guardar ajuste</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: confirmar CTS -->
    <wz-modal v-model="confirmModal" title="Confirmar CTS">
      <p>
        ¿Confirmar la CTS
        <strong>{{ periodLabel(result.periodType) }} {{ result.periodYear }}</strong>?
        Una vez confirmada no se podrá recalcular ni editar.
      </p>
      <template #footer>
        <wz-button variant="outline" @click="confirmModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="doConfirm">Confirmar CTS</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { CtsService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  data() {
    return {
      result: { details: [] },
      adjustModal: false,
      adjustTarget: null,
      adjustForm: { adjustment: null, reason: '' },
      adjustReasonError: '',
      confirmModal: false,
    }
  },
  computed: {
    isBorrador() {
      return this.result.status === Constants.CtsStatus.BORRADOR.key
    },
    noAccountCount() {
      return (this.result.details || []).filter(d => !d.isExcluded && !d.hasCtsAccount).length
    },
  },
  methods: {
    periodLabel(type) {
      const t = Constants.CtsPeriodType[type]
      return t ? t.label.split(' —')[0] : type
    },
    statusLabel(status) {
      const s = Constants.CtsStatus[status]
      return s ? s.label : status
    },
    statusVariant(status) {
      const s = Constants.CtsStatus[status]
      return s ? s.color : 'dark'
    },
    ctsPayable(d) {
      return Number(d.ctsAmount || 0) + Number(d.manualAdjustment || 0)
    },
    maskAccount(account) {
      if (!account) return ''
      return `****${String(account).slice(-4)}`
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.getPeriodDetail(this.$route.params.id)
        this.result = resp.payload || { details: [] }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el período')
        this.$router.push({ name: 'cts-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async toggleExclude(d) {
      const exclude = !d.isExcluded
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.excludeDetail(d.id, exclude, null)
        this.result = resp.payload || this.result
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al actualizar la exclusión')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    openAdjust(d) {
      this.adjustTarget = d
      this.adjustForm = { adjustment: d.manualAdjustment || null, reason: d.adjustmentReason || '' }
      this.adjustReasonError = ''
      this.adjustModal = true
    },
    async saveAdjust() {
      this.adjustReasonError = ''
      if (!this.adjustForm.reason || !this.adjustForm.reason.trim()) {
        this.adjustReasonError = 'El motivo es obligatorio'
        return
      }
      const adj = this.adjustForm.adjustment != null ? Number(this.adjustForm.adjustment) : 0
      const newTotal = Number(this.adjustTarget.ctsAmount || 0) + adj
      if (newTotal < 0) {
        Alert.error('El ajuste dejaría la CTS en negativo. Corrija el monto.')
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.adjustDetail(this.adjustTarget.id, {
          adjustment: adj,
          reason: this.adjustForm.reason,
        })
        this.result = resp.payload || this.result
        this.adjustModal = false
        Alert.success('Ajuste guardado')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el ajuste')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doConfirm() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.confirm(this.result.id)
        this.result = resp.payload || this.result
        this.confirmModal = false
        Alert.success('CTS confirmada correctamente')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al confirmar la CTS')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadXlsx() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.downloadXlsx(this.result.id)
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `cts-${(this.result.periodType || '').toLowerCase()}-${this.result.periodYear}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        Alert.error('Error al exportar el Excel')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>

<style scoped>
.row-excluded {
  opacity: 0.5;
}
</style>
